import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchOpportunities();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [filter, heroImages.length]);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filter !== 'all') {
        params.type = filter;
      }
      
      const response = await axios.get(API_ENDPOINTS.OPPORTUNITIES, { params });
      if (response.data.success) {
        setOpportunities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      // Fallback data for development
      setOpportunities([
        {
          id: 1,
          title: 'Program Manager - Peace Building',
          description: 'We are seeking an experienced Program Manager to lead our peace building initiatives in the North Rift region.',
          content: 'Responsibilities include program planning, implementation, monitoring, and reporting...',
          type: 'job',
          location: 'Kapenguria, West Pokot',
          deadline: '2024-12-31',
          requirements: 'Bachelor\'s degree in relevant field, 5+ years experience',
          is_active: true
        },
        {
          id: 2,
          title: 'Community Mobilization Volunteer',
          description: 'Volunteer opportunity to work with communities on development projects.',
          content: 'Assist in community mobilization, awareness campaigns, and data collection...',
          type: 'volunteer',
          location: 'Turkana County',
          deadline: '2024-06-30',
          requirements: 'Diploma in community development, willingness to travel',
          is_active: true
        },
        {
          id: 3,
          title: 'Tender: Supply of Farm Inputs',
          description: 'Tender for supply of agricultural inputs to support our livelihoods program.',
          content: 'Supply of seeds, fertilizers, and farm tools for distribution to farmers...',
          type: 'tender',
          location: 'Multiple Counties',
          deadline: '2024-05-15',
          requirements: 'Registered company with valid licenses',
          is_active: true
        },
        {
          id: 4,
          title: 'Monitoring & Evaluation Officer',
          description: 'M&E Officer to track program progress and impact.',
          content: 'Develop M&E frameworks, conduct field assessments, and prepare reports...',
          type: 'job',
          location: 'Nairobi with field travel',
          deadline: '2024-11-30',
          requirements: 'Degree in M&E or related field, 3+ years experience',
          is_active: true
        },
        {
          id: 5,
          title: 'Health Program Intern',
          description: 'Internship opportunity in our health and nutrition program.',
          content: 'Support health education activities, data entry, and report writing...',
          type: 'volunteer',
          location: 'Samburu County',
          deadline: '2024-08-31',
          requirements: 'Recent graduate in public health or related field',
          is_active: true
        },
        {
          id: 6,
          title: 'Tender: Construction of Water Pans',
          description: 'Tender for construction of water harvesting structures.',
          content: 'Construction of 5 water pans in drought-prone areas...',
          type: 'tender',
          location: 'Baringo County',
          deadline: '2024-07-20',
          requirements: 'Construction company with relevant experience',
          is_active: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    switch(type) {
      case 'job': return { class: 'bg-success', label: 'Job' };
      case 'volunteer': return { class: 'bg-info', label: 'Volunteer' };
      case 'tender': return { class: 'bg-warning text-dark', label: 'Tender' };
      default: return { class: 'bg-secondary', label: type };
    }
  };

  // Filter opportunities based on selected filter
  const filteredOpportunities = filter === 'all' 
    ? opportunities 
    : opportunities.filter(opp => opp.type === filter);

  return (
    <div className="opportunities-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">Opportunities</h1>
              <p className="lead text-white mb-0">
                Join our team and contribute to sustainable development
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All Opportunities
                </button>
                <button 
                  className={`btn ${filter === 'job' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('job')}
                >
                  Jobs
                </button>
                <button 
                  className={`btn ${filter === 'volunteer' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('volunteer')}
                >
                  Volunteer
                </button>
                <button 
                  className={`btn ${filter === 'tender' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('tender')}
                >
                  Tenders
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities List */}
      <section className="section-padding">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredOpportunities.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-briefcase display-1 text-muted mb-4"></i>
              <h3>No opportunities available</h3>
              <p className="text-muted mb-4">
                {filter !== 'all' ? `No ${filter} opportunities at the moment.` : 'Check back later for new opportunities'}
              </p>
              {filter !== 'all' && (
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => setFilter('all')}
                >
                  View All Opportunities
                </button>
              )}
              <p className="mt-3">
                You can still <a href="/contact" className="text-primary">contact us</a> to express your interest
                in working with SIKOM.
              </p>
            </div>
          ) : (
            <div className="row">
              <div className="col-lg-8 mx-auto">
                {filteredOpportunities.map((opportunity) => {
                  const badge = getTypeBadge(opportunity.type);
                  const deadline = new Date(opportunity.deadline);
                  const isExpired = deadline < new Date();
                  const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div className="card border-0 shadow-sm mb-4 hover-shadow" key={opportunity.id}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="card-title mb-1">{opportunity.title}</h5>
                            <span className={`badge ${badge.class} me-2`}>{badge.label}</span>
                            {opportunity.location && (
                              <span className="text-muted">
                                <i className="bi bi-geo-alt me-1"></i>
                                {opportunity.location}
                              </span>
                            )}
                          </div>
                          <div className="text-end">
                            {isExpired ? (
                              <span className="badge bg-danger">Expired</span>
                            ) : daysLeft <= 7 ? (
                              <span className="badge bg-warning text-dark">Closing Soon</span>
                            ) : (
                              <span className="badge bg-success">Open</span>
                            )}
                          </div>
                        </div>
                        
                        <p className="card-text text-muted mb-3">
                          {opportunity.description || 'No description available.'}
                        </p>
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              <i className="bi bi-calendar me-1"></i>
                              Deadline: {deadline.toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                              {!isExpired && daysLeft > 0 && (
                                <span className="ms-2">
                                  ({daysLeft} day{daysLeft !== 1 ? 's' : ''} left)
                                </span>
                              )}
                            </small>
                          </div>
                          
                          <div>
                            <Link 
                              to={`/opportunities/${opportunity.slug || opportunity.id}`}
                              className="btn btn-primary btn-sm"
                            >
                              View Details <i className="bi bi-arrow-right ms-1"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Application Instructions */}
      <section className="py-4 bg-primary text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h3 className="h4 mb-3">How to Apply</h3>
              <p className="mb-0">
                Interested candidates should send their application (cover letter and CV) to 
                <strong> sikompeace@yahoo.com</strong> with the position title as the subject.
              </p>
              <p className="mt-2 mb-0">
                For tenders, download the full tender document from the opportunity details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="display-5 fw-bold mb-4">Why Work With SIKOM?</h2>
              <div className="row mt-5">
                <div className="col-md-4 mb-4">
                  <div className="p-4">
                    <i className="bi bi-heart display-4 text-primary mb-3"></i>
                    <h5>Make a Difference</h5>
                    <p className="text-muted">Contribute to meaningful community development</p>
                  </div>
                </div>
                <div className="col-md-4 mb-4">
                  <div className="p-4">
                    <i className="bi bi-people display-4 text-primary mb-3"></i>
                    <h5>Team Environment</h5>
                    <p className="text-muted">Work with passionate professionals</p>
                  </div>
                </div>
                <div className="col-md-4 mb-4">
                  <div className="p-4">
                    <i className="bi bi-graph-up display-4 text-primary mb-3"></i>
                    <h5>Career Growth</h5>
                    <p className="text-muted">Opportunities for professional development</p>
                  </div>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6 mb-4">
                  <div className="p-4">
                    <i className="bi bi-globe display-4 text-primary mb-3"></i>
                    <h5>Field Experience</h5>
                    <p className="text-muted">Gain hands-on experience in development work</p>
                  </div>
                </div>
                <div className="col-md-6 mb-4">
                  <div className="p-4">
                    <i className="bi bi-award display-4 text-primary mb-3"></i>
                    <h5>Capacity Building</h5>
                    <p className="text-muted">Regular training and skills development</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Opportunities */}
      <section className="py-5">
        <div className="container text-center">
          <h2 className="display-5 fw-bold mb-4">Not Seeing the Right Opportunity?</h2>
          <p className="lead mb-5">
            Send us your CV and we'll keep you in mind for future opportunities
          </p>
          <div className="d-flex justify-content-center gap-3">
            <a href="mailto:sikompeace@yahoo.com?subject=General%20Application" className="btn btn-primary btn-lg px-5">
              <i className="bi bi-envelope me-2"></i> Send Your CV
            </a>
            <a href="/contact" className="btn btn-outline-primary btn-lg px-5">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Opportunities;